import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  OnboardingContextActionType,
  useOnboardingContext,
  useOnboardingDispatch
} from '@/contexts/onboardingContext';
import useMixpanel from '@/hooks/useTracking/useMixpanel';
import useTranslate from '@/hooks/useTranslate';
import { InsuranceType } from '@/pages/members/constants/insurance';
import { useSubstances } from '@/pages/members/hooks/useSubstances';
import { useTreatableSubstances } from '@/pages/members/hooks/useTreatableSubstances';
import { useTreatableTreatmentPreferences } from '@/pages/members/hooks/useTreatableTreatmentPreferences';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import { StepProps } from '@/pages/members/pages/Onboarding/steps/types';
import getSubmittedData from '@/pages/members/pages/Onboarding/utils/getSubmittedData';
import Alert from '@/ui/members/components/Alert';
import AlertBanner from '@/ui/members/components/AlertBanner';
import FieldError from '@/ui/members/components/form/FieldError';
import InputGroup from '@/ui/members/components/form/InputGroup';
import RadioGroup from '@/ui/members/components/form/RadioGroup';
import SelectDialog from '@/ui/members/components/form/SelectDialog/SelectDialog';
import Spinner from '@/ui/members/components/Spinner';
import Heading from '@/ui/members/components/typography/Heading/Heading';

import { Needs, PrimarySubstanceFormValues } from './types';
import { groupTreatableSubstancesByNeeds } from './utils/groupTreatableSubstancesByNeeds';

interface Props extends StepProps {
  /**
   * Whether to show all content or a minimal version
   */
  showAllContent?: boolean;
  subheaderTextKey?: string;
  showTreatableMatchesText?: boolean;
}

/**
 * This is a copy of the "What do you need help with?" step but with
 * additional handling for treatment preference.
 * The call for treatable treatment options happens here.
 *
 * If there are multiple options available they will be stored in the context
 *
 * If the user goes back to the previous step and changes their primary substance
 * the treatment preference will be reset and the user will have to select again
 * from the updated list of options.
 *
 * These additions help the version TreatmentPreferenceNode
 * use logic to determine whether to show the treatment preference screen
 *
 *
 * @param nodeType
 * @param uid
 * @constructor
 */
const WhatDoYouNeedHelpWithWithTreatmentPreferenceStep = ({
  nodeType,
  showAllContent = true,
  uid,
  subheaderTextKey = 'weWillDiscussYourGoals',
  showTreatableMatchesText = true
}: Props) => {
  const onboardingContext = useOnboardingContext();
  const dispatch = useOnboardingDispatch();
  const { jurisdiction } = onboardingContext.data;
  const mixpanel = useMixpanel();
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.WhatDoYouNeedHelpWithStep`);
  const {
    data: { insuranceType }
  } = onboardingContext;

  const {
    data: substanceResponse,
    isPending: isPendingSubstances,
    isError: isErrorSubstances
  } = useSubstances();

  const substanceDrugCategoryMap = useMemo(() => {
    return substanceResponse?.reduce(
      (acc, substance) => {
        acc[substance.name] = substance.drug_category as string;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [substanceResponse]);

  const {
    data: treatableSubstanceResponse,
    isPending: isPendingTreatableSubstances,
    isError: isErrorTreatableSubstances
  } = useTreatableSubstances({ jurisdiction: jurisdiction as string });

  const treatableSubstancesByNeeds = useMemo(() => {
    if (!substanceResponse || !treatableSubstanceResponse?.results) {
      return {
        [Needs.MentalHealth]: [],
        [Needs.SubstanceUse]: []
      };
    }

    return groupTreatableSubstancesByNeeds(treatableSubstanceResponse?.results, substanceResponse);
  }, [substanceResponse, treatableSubstanceResponse?.results]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch
  } = useForm<PrimarySubstanceFormValues>({
    mode: 'onSubmit',
    defaultValues: (getSubmittedData(
      nodeType,
      uid,
      onboardingContext.steps
    ) as PrimarySubstanceFormValues) || {
      drugCategory: '',
      primarySubstance: '',
      programScope: null as Needs | null,
      treatmentPreference: undefined
    }
  });
  const values = watch();

  const hasTreatableMatches =
    treatableSubstanceResponse?.results && treatableSubstanceResponse.results.length > 0;

  const { data: treatableTreatmentPreferences } = useTreatableTreatmentPreferences({
    drug_category: values.drugCategory,
    jurisdiction: jurisdiction as string
  });

  useEffect(() => {
    setValue('treatmentPreference', undefined); // Reset treatment preference when options change
    if (treatableTreatmentPreferences) {
      dispatch({
        type: OnboardingContextActionType.SET_FORM_OPTIONS,
        payload: { treatableTreatmentPreferences }
      });
    }
  }, [treatableTreatmentPreferences, dispatch, setValue]);

  const { programScope } = values;

  const treatableSubstancesOnBothCategories =
    treatableSubstancesByNeeds[Needs.MentalHealth].length > 0 &&
    treatableSubstancesByNeeds[Needs.SubstanceUse].length > 0;

  useEffect(() => {
    if (treatableSubstancesOnBothCategories) {
      return;
    }

    const mentalHealthTreatableSubstances = treatableSubstancesByNeeds[Needs.MentalHealth];
    const substanceUseTreatableSubstances = treatableSubstancesByNeeds[Needs.SubstanceUse];

    if (!substanceUseTreatableSubstances.length && !!mentalHealthTreatableSubstances.length) {
      // Defaults selected category to Mental Health if no Substance Use options are available
      setValue('programScope', Needs.MentalHealth);
      mixpanel.track('Onboarding Program Auto-Selection: Mental Health', {
        jurisdiction,
        treatableSubstances: treatableSubstanceResponse?.results,
        version: onboardingContext.version,
        feature: 'unified-onboarding'
      });
    }

    if (!mentalHealthTreatableSubstances.length && !!substanceUseTreatableSubstances.length) {
      // Defaults selected category to Substance Use if no mental health options are available
      setValue('programScope', Needs.SubstanceUse);
      mixpanel.track('Onboarding Program Auto-Selection: Substance Use', {
        jurisdiction,
        treatableSubstances: treatableSubstanceResponse?.results,
        version: onboardingContext.version,
        feature: 'unified-onboarding'
      });
    }
  }, [treatableSubstancesOnBothCategories, treatableSubstancesByNeeds, setValue, jurisdiction]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTextBySelectedNeed = () => {
    if (programScope === Needs.SubstanceUse) {
      return {
        label: t('primarySubstanceLabel'),
        placeholder: t('primarySubstancePlaceholder'),
        dialogHeading: t('substanceDialogHeading'),
        dialogSubHeading: t('substanceDialogSubheading')
      };
    } else if (programScope === Needs.MentalHealth) {
      return {
        label: t('primaryChallengeLabel'),
        placeholder: t('primaryChallengePlaceholder'),
        dialogHeading: t('challengeDialogHeading'),
        dialogSubHeading: t('challengeDialogSubheading')
      };
    }

    return {
      label: '',
      placeholder: '',
      dialogHeading: '',
      dialogSubHeading: ''
    };
  };
  const { label, placeholder, dialogHeading, dialogSubHeading } = getTextBySelectedNeed();

  const isPending = isPendingTreatableSubstances || isPendingSubstances;
  const isError = isErrorTreatableSubstances || isErrorSubstances;

  const clearPrimarySubstanceAndDrugCategory = () => {
    setValue('primarySubstance', '');
    setValue('drugCategory', '');
  };

  return (
    <StepComponent handleSubmit={handleSubmit} uid={uid} nodeType={nodeType} isValid={isValid}>
      {isError && (
        <Alert level="error">
          <p>{t('errorFetching')}</p>
        </Alert>
      )}

      <div className="text-center mb-4">
        <Heading value={1}>{t('whatDoYouNeedHelpWith')}</Heading>

        {insuranceType === InsuranceType.MEDICAID && (
          <div className="text-left my-2">
            <AlertBanner text={t('medicaidInfo')} />
          </div>
        )}

        {showAllContent && <p className="text-base mb-3">{t(subheaderTextKey)}</p>}
        {showTreatableMatchesText && hasTreatableMatches && (
          <p>{t('treatableListDescription', { jurisdiction })}</p>
        )}
      </div>

      {treatableSubstancesOnBothCategories && (
        <InputGroup>
          <Controller
            name="programScope"
            rules={{
              required: t('drugCategoryIsRequired')
            }}
            control={control}
            render={({ field }) => (
              <>
                {isPending ? (
                  <Spinner />
                ) : (
                  <RadioGroup
                    autoFocus
                    optionsClassName="grid gap-2 mb-6"
                    id="programScope"
                    onChange={(value) => {
                      clearPrimarySubstanceAndDrugCategory();
                      field.onChange(value);
                    }}
                    options={[
                      {
                        label: t('mentalHealth'),
                        value: Needs.MentalHealth
                      },
                      {
                        label: t('substanceUse'),
                        value: Needs.SubstanceUse
                      }
                    ]}
                    selectedValue={field.value}
                  />
                )}
              </>
            )}
          />
          <FieldError error={errors.programScope} />
        </InputGroup>
      )}

      {programScope && (
        <InputGroup>
          <Controller
            defaultValue=""
            name="primarySubstance"
            control={control}
            rules={{
              required: `${label} ${t('isRequired')}`
            }}
            render={({ field }) => (
              <>
                {isPending ? (
                  <Spinner />
                ) : (
                  <SelectDialog
                    autoFocus={!treatableSubstancesOnBothCategories}
                    dialogHeading={dialogHeading}
                    dialogSubHeading={dialogSubHeading}
                    id="primary-substance"
                    label={label}
                    onChange={(e: string | null) => {
                      field.onChange(e);
                      setValue(
                        'drugCategory',
                        substanceDrugCategoryMap && e ? substanceDrugCategoryMap[e] : ''
                      );
                    }}
                    options={treatableSubstancesByNeeds[programScope as Needs]}
                    placeholder={placeholder}
                    selected={field.value}
                  />
                )}
              </>
            )}
          />

          <FieldError error={errors.primarySubstance} />
        </InputGroup>
      )}
    </StepComponent>
  );
};

export default WhatDoYouNeedHelpWithWithTreatmentPreferenceStep;
